import React, { useEffect, useRef, useState } from 'react';
import { formatDateCa } from '../legal-board/legalBoardUi';
import { fetchReport, preloadReport, cachedReport, forgetReport, saveReport, downloadReportPdf, downloadReportText, downloadIncomePdf } from './api';
import { Disclosure } from '../legal-board/LegalBoardPrimitives';
import { REPORT_GUIDE, MISSING_CASTELLS_NOTE, ACTIVITY_GUIDE } from './reportGuide';
import { StepScreen } from './PublicUtilityForms';
import PublicUtilitySigning from './PublicUtilitySigning';

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

const GROUPS = [
    { key: 'actuació', label: 'Actuacions', open: true },
    { key: 'assaig', label: 'Assajos', open: false },
    { key: 'other', label: 'Altres activitats', open: true },
];
const groupOf = (event) => (event.type === 'actuació' || event.type === 'assaig' ? event.type : 'other');

// A checkbox that can also say «some of them»
function GroupCheckbox({ checked, indeterminate, ...props }) {
    const ref = useRef(null);
    useEffect(() => {
        if (ref.current) ref.current.indeterminate = indeterminate;
    }, [indeterminate]);
    return <input ref={ref} type="checkbox" checked={checked} aria-checked={indeterminate ? 'mixed' : checked} {...props} />;
}

/**
 * Which of the year's activities go in the memòria: one group per kind, each
 * with a checkbox for all of them and one per activity.
 */
function ActivityPicker({ events, excluded, setExcluded, disabled }) {
    const [open, setOpen] = useState(() => Object.fromEntries(GROUPS.map((group) => [group.key, group.open])));
    const toggle = (ids, include) => setExcluded((current) => {
        const next = new Set(current);
        ids.forEach((id) => (include ? next.delete(id) : next.add(id)));
        return next;
    });
    return (
        <div className="public-utility-picker">
            {GROUPS.map((group) => {
                const items = events.filter((event) => groupOf(event) === group.key);
                if (items.length === 0) return null;
                const included = items.filter((event) => !excluded.has(event.id)).length;
                const ids = items.map((event) => event.id);
                return (
                    <div key={group.key} className="public-utility-picker-group">
                        <div className="public-utility-picker-head">
                            <label>
                                <GroupCheckbox
                                    checked={included === items.length}
                                    indeterminate={included > 0 && included < items.length}
                                    disabled={disabled}
                                    onChange={(event) => toggle(ids, event.target.checked)}
                                    aria-label={`Totes les ${group.label.toLowerCase()}`}
                                />
                                <strong>{group.label}</strong>
                            </label>
                            <button type="button" className="public-utility-picker-toggle" aria-expanded={Boolean(open[group.key])} onClick={() => setOpen((current) => ({ ...current, [group.key]: !current[group.key] }))}>
                                <span className="legal-board-muted legal-board-small">{included === items.length ? items.length : `${included} de ${items.length}`}</span>
                                <i className={`fas fa-chevron-${open[group.key] ? 'up' : 'down'}`} aria-hidden="true"></i>
                            </button>
                        </div>
                        {open[group.key] ? (
                            <ul className="public-utility-picker-list" aria-label={group.label}>
                                {items.map((event) => (
                                    <li key={event.id}>
                                        <label className={excluded.has(event.id) ? 'off' : ''}>
                                            <input type="checkbox" checked={!excluded.has(event.id)} disabled={disabled} onChange={(change) => toggle([event.id], change.target.checked)} />
                                            <span className="public-utility-picker-date">{formatDateCa(event.day)}</span>
                                            <span className="public-utility-picker-title">{event.title || (group.key === 'assaig' ? 'Assaig' : 'Activitat')}</span>
                                        </label>
                                    </li>
                                ))}
                            </ul>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
}

/**
 * The memòria d'activitats of one year, one screen at a time: what Aleta puts
 * in, then each of the junta's texts, then a summary with the PDF. Every
 * «Següent» saves what was written.
 */
const excludedOf = (report) => new Set((report?.events || []).filter((event) => !event.included).map((event) => event.id));

function PublicUtilityReport({ userId, year, canManage, onBack }) {
    // Preloaded by the tab: the first screen shows at once
    const preloaded = cachedReport({ userId, year })?.report || null;
    const [report, setReport] = useState(preloaded);
    const [texts, setTexts] = useState(preloaded?.texts || {});
    const [excluded, setExcluded] = useState(() => excludedOf(preloaded));
    const [examplesOpen, setExamplesOpen] = useState({});
    const [guideOpen, setGuideOpen] = useState(false);
    const [screen, setScreen] = useState(0);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let cancelled = false;
        // Still on its way from the tab's preload: wait for that one instead of asking again
        if (!cachedReport({ userId, year })) {
            preloadReport({ userId, year })
                .then((payload) => {
                    if (cancelled) return;
                    setReport(payload.report);
                    setTexts(payload.report.texts);
                    setExcluded(excludedOf(payload.report));
                })
                .catch((loadError) => { if (!cancelled) setError(loadError.message || 'No s’ha pogut carregar la memòria.'); });
        }
        return () => {
            cancelled = true;
            forgetReport({ userId, year });
        };
    }, [userId, year]);

    if (!report) {
        return (
            <section className="legal-board-card">
                <button type="button" className="legal-board-button link" onClick={onBack}>← Tornar</button>
                {error ? <div className="legal-board-alert error" role="alert">{error}</div> : <div className="legal-board-spinner" role="status" aria-label="Carregant"></div>}
            </section>
        );
    }

    const { fields, data } = report;
    const events = report.events || [];
    // Intro, activities, one screen per text, summary, and signing when it is on
    const signingEnabled = Boolean(report.signingEnabled);
    const total = fields.length + (signingEnabled ? 4 : 3);
    const savedExcluded = new Set(events.filter((event) => !event.included).map((event) => event.id));
    const excludedChanged = excluded.size !== savedExcluded.size || [...excluded].some((id) => !savedExcluded.has(id));
    const dirty = excludedChanged || fields.some((field) => (texts[field.key] || '') !== (report.texts[field.key] || ''));
    const count = (type) => events.filter((event) => groupOf(event) === type && !excluded.has(event.id)).length;

    const run = async (action) => {
        if (busy) return;
        setBusy(true);
        setError(null);
        try {
            await action();
        } catch (actionError) {
            setError(actionError.message || 'No s’ha pogut completar.');
        } finally {
            setBusy(false);
        }
    };

    // What is written is saved before moving on, so nothing is lost when leaving halfway
    const save = async () => {
        await saveReport({ userId, year, texts, excludedEvents: [...excluded] });
        if (excludedChanged) {
            // The participants and castells are counted again without the unticked ones
            const payload = await fetchReport({ userId, year });
            setReport(payload.report);
        } else {
            setReport((current) => ({ ...current, texts, saved: true }));
        }
    };

    const go = (next) => run(async () => {
        if (canManage && dirty) await save();
        setScreen(next);
    });

    const exit = () => run(async () => {
        if (canManage && dirty) await saveReport({ userId, year, texts, excludedEvents: [...excluded] });
        onBack();
    });

    const errorBox = error ? <div className="legal-board-alert error" role="alert">{error}</div> : null;

    if (screen === 0) {
        return (
            <StepScreen id="public-utility-report" title={`Memòria d’activitats ${year}`} step={1} total={total} onExit={exit} onNext={() => go(1)} nextLabel="Començar" busy={busy}>
                <p>Aleta ja hi posa el que sap de {year}:</p>
                <ul className="public-utility-facts">
                    <li>{plural(count('actuació'), 'actuació', 'actuacions')}, amb la data i el lloc</li>
                    <li>{plural(count('assaig'), 'assaig', 'assajos')}{count('other') > 0 ? ` i ${plural(count('other'), 'activitat més', 'activitats més')}` : ''}</li>
                    <li>{plural(data.participants.total, 'persona hi ha participat', 'persones hi han participat')}</li>
                    <li>{plural(data.payingMembers, 'soci amb la quota pagada', 'socis amb la quota pagada')}</li>
                </ul>
                <p className="legal-board-muted">Al pas següent pots treure les activitats que no hi toquin. Després hi afegeixes {fields.length} textos curts, un per pantalla; alguns ja porten una proposta.</p>
                {errorBox}
            </StepScreen>
        );
    }

    if (screen === 1) {
        return (
            <StepScreen id="public-utility-report" title="Quines activitats hi surten" step={2} total={total} onExit={exit} onBack={() => go(0)} onNext={() => go(2)} busy={busy}>
                <p className="legal-board-muted">{ACTIVITY_GUIDE.rule} El que desmarquis no surt a la memòria ni compta en les xifres.</p>
                <Disclosure id="public-utility-activity-guide" summary="Què hi ha de sortir i què no" open={guideOpen} onToggle={() => setGuideOpen((value) => !value)}>
                    <div className="public-utility-activity-guide">
                        <p className="public-utility-guide-label">Sí</p>
                        <ul>{ACTIVITY_GUIDE.yes.map((item) => <li key={item}>{item}</li>)}</ul>
                        <p className="public-utility-guide-label">No</p>
                        <ul>{ACTIVITY_GUIDE.no.map((item) => <li key={item}>{item}</li>)}</ul>
                        <p className="public-utility-guide-label">Depèn</p>
                        <ul>{ACTIVITY_GUIDE.doubt.map((item) => <li key={item}>{item}</li>)}</ul>
                        <p className="legal-board-muted legal-board-small">Si en treus alguna que val la pena explicar, la pots esmentar en una frase a «Activitats de l’any».</p>
                    </div>
                </Disclosure>
                <ActivityPicker events={events} excluded={excluded} setExcluded={setExcluded} disabled={!canManage || busy} />
                {errorBox}
            </StepScreen>
        );
    }

    if (screen <= fields.length + 1) {
        const field = fields[screen - 2];
        const guide = REPORT_GUIDE[field.key] || {};
        const text = texts[field.key] || '';
        // The example starts open while there is nothing written
        const exampleOpen = examplesOpen[field.key] ?? !text.trim();
        return (
            <StepScreen
                id="public-utility-report"
                title={field.label}
                step={screen + 1}
                total={total}
                onExit={exit}
                onBack={() => go(screen - 1)}
                onNext={() => go(screen + 1)}
                busy={busy}
            >
                {guide.asks ? (
                    <p className="public-utility-guide-asks">
                        <span className="public-utility-guide-label">Què demana la Generalitat</span>
                        {guide.asks}
                    </p>
                ) : null}
                {guide.aleta ? (
                    <p className="legal-board-muted legal-board-small public-utility-guide-aleta">
                        <i className="fas fa-check" aria-hidden="true"></i> Aleta ja hi posa {guide.aleta(data)}
                    </p>
                ) : null}
                {field.key === 'results' && data.actuacions > 0 && data.castells === 0 ? <p className="legal-board-muted legal-board-small">{MISSING_CASTELLS_NOTE}</p> : null}
                <label className="legal-board-sr-only" htmlFor={`public-utility-report-${field.key}`}>{field.label}</label>
                <textarea
                    id={`public-utility-report-${field.key}`}
                    rows={7}
                    value={text}
                    disabled={!canManage || busy}
                    onChange={(event) => setTexts((current) => ({ ...current, [field.key]: event.target.value }))}
                />
                {guide.example ? (
                    <Disclosure
                        id={`public-utility-example-${field.key}`}
                        summary="Veure un exemple"
                        open={exampleOpen}
                        onToggle={() => setExamplesOpen((current) => ({ ...current, [field.key]: !exampleOpen }))}
                    >
                        <p className="public-utility-example">{guide.example}</p>
                        <p className="legal-board-muted legal-board-small">
                            Canvia el que va entre [claudàtors] pel que toqui a la colla.
                            {canManage && !text.trim() ? (
                                <>
                                    {' '}<button type="button" className="legal-board-inline-action" disabled={busy} onClick={() => setTexts((current) => ({ ...current, [field.key]: guide.example }))}>Fer servir l’exemple</button>
                                </>
                            ) : null}
                        </p>
                    </Disclosure>
                ) : null}
                {errorBox}
            </StepScreen>
        );
    }

    const empty = fields.filter((field) => !(texts[field.key] || '').trim());
    const summaryScreen = fields.length + 2;
    if (signingEnabled && screen > summaryScreen) {
        return (
            <StepScreen id="public-utility-report" title="Signar la memòria" step={total} total={total} onExit={exit} onBack={() => go(summaryScreen)} busy={busy}>
                <PublicUtilitySigning
                    userId={userId}
                    year={year}
                    report={report}
                    canManage={canManage}
                    busy={busy}
                    run={run}
                    onSigning={(signing) => setReport((current) => ({ ...current, signing }))}
                />
                {errorBox}
            </StepScreen>
        );
    }

    return (
        <StepScreen
            id="public-utility-report"
            title={`La memòria de ${year}`}
            step={summaryScreen + 1}
            total={total}
            onExit={exit}
            onBack={() => go(fields.length + 1)}
            onNext={signingEnabled ? () => go(summaryScreen + 1) : () => run(() => downloadReportPdf({ userId, year }))}
            nextLabel={signingEnabled ? 'Signar-la' : 'Descarregar la memòria'}
            nextIcon={signingEnabled ? null : 'fa-download'}
            nextDisabled={empty.length > 0}
            busy={busy}
        >
            {empty.length === 0 ? (
                signingEnabled ? (
                    <p className="legal-board-muted">Tots els apartats tenen text. Pica’n un per tornar-hi, o{' '}
                        <button type="button" className="legal-board-inline-action" onClick={() => run(() => downloadReportPdf({ userId, year }))} disabled={busy}>descarrega-la per revisar-la</button>.
                    </p>
                ) : (
                    <p className="legal-board-muted">Tots els apartats tenen text. Pica’n un per tornar-hi.</p>
                )
            ) : (
                <p className="public-utility-incomplete" role="status">
                    {empty.length === 1 ? 'Falta 1 apartat per escriure' : `Falten ${empty.length} apartats per escriure`}. La Generalitat demana tots els apartats: la memòria es podrà descarregar quan hi siguin.
                </p>
            )}
            <ul className="public-utility-tap-list" aria-label="Apartats">
                <li>
                    <button type="button" className="public-utility-tap-row" onClick={() => go(1)} disabled={busy}>
                        <span className="public-utility-tap-text">
                            <strong>Activitats</strong>
                            <span className="public-utility-tap-sub">
                                {plural(count('actuació'), 'actuació', 'actuacions')} · {plural(count('assaig'), 'assaig', 'assajos')} · {plural(count('other'), 'activitat més', 'activitats més')}
                                {excluded.size > 0 ? ` · ${plural(excluded.size, 'desmarcada', 'desmarcades')}` : ''}
                            </span>
                        </span>
                        <span></span>
                        <i className="fas fa-chevron-right" aria-hidden="true"></i>
                    </button>
                </li>
                {fields.map((field, index) => {
                    const text = (texts[field.key] || '').trim();
                    return (
                        <li key={field.key}>
                            <button type="button" className="public-utility-tap-row" onClick={() => go(index + 2)} disabled={busy}>
                                <span className="public-utility-tap-text">
                                    <strong>{field.label}</strong>
                                    <span className={`public-utility-tap-sub ${text ? '' : 'warn'}`}>{text || 'Sense escriure'}</span>
                                </span>
                                <span></span>
                                <i className="fas fa-chevron-right" aria-hidden="true"></i>
                            </button>
                        </li>
                    );
                })}
            </ul>
            <p className="legal-board-small">
                La Generalitat la demana signada i també en format digital:{' '}
                <button type="button" className="legal-board-inline-action" onClick={() => run(() => downloadReportText({ userId, year }))} disabled={busy || empty.length > 0}>descarrega-la en text</button>
                {' '}per adjuntar-la al tràmit.
            </p>
            <p className="legal-board-small">
                Per al gestor, els{' '}
                <button type="button" className="legal-board-inline-action" onClick={() => run(() => downloadIncomePdf({ userId, year }))} disabled={busy}>ingressos de {year}</button>.
            </p>
            {errorBox}
        </StepScreen>
    );
}

export default PublicUtilityReport;
